import { drawBananaSprite } from './projectile.js';

export class ReplayManager {
  constructor(game) {
    this.game = game;

    // Recording state
    this.isRecording = false;
    this.initialTerrainSnapshot = null;
    this.recordedFrames = [];
    this.recordedCarves = [];
    this.recordedAudio = [];
    this.turnMetadata = null;
    this.currentFrame = 0;
    this.actionStartFrame = null;

    // Stored Winning Replay
    this.winningReplay = null;

    // Playback state
    this.isReplaying = false;
    this.playbackIndex = 0;
    this.playbackSpeed = 1.0; // 0.5, 1.0, 1.25, 1.5
    this.isPaused = false;
    this.appliedCarveIndex = 0;
    this.appliedAudioIndex = 0;
    this.playbackTerrainCanvas = null;
    this.playbackTerrainCtx = null;
    this.onReplayComplete = null;
    this.endHoldTimer = 0;
  }

  // ─── Recording Methods ────────────────────────────────────────────────────────

  startTurnRecording(activeWorm, activeTeam, currentWeapon, windStrength = 0) {
    if (this.isReplaying) return;

    // 1. Snapshot initial terrain for this turn
    if (this.game.terrain && this.game.terrain.canvas) {
      if (!this.initialTerrainSnapshot) {
        this.initialTerrainSnapshot = document.createElement('canvas');
      }
      this.initialTerrainSnapshot.width = this.game.terrain.width;
      this.initialTerrainSnapshot.height = this.game.terrain.height;
      const sCtx = this.initialTerrainSnapshot.getContext('2d');
      sCtx.clearRect(0, 0, this.game.terrain.width, this.game.terrain.height);
      sCtx.drawImage(this.game.terrain.canvas, 0, 0);
    }

    // 2. Reset recording buffers
    this.recordedFrames = [];
    this.recordedCarves = [];
    this.recordedAudio = [];
    this.currentFrame = 0;
    this.actionStartFrame = null;

    this.turnMetadata = {
      activeWormName: activeWorm ? activeWorm.name : 'Unknown Worm',
      activeTeamName: activeTeam ? activeTeam.name : 'Unknown Team',
      activeTeamColor: activeTeam ? activeTeam.color : '#ef4444',
      weaponId: currentWeapon ? currentWeapon.id : 'bazooka',
      weaponName: currentWeapon ? currentWeapon.name : 'Bazooka',
      weaponIcon: currentWeapon ? currentWeapon.icon : '🚀',
      windStrength: windStrength
    };

    this.isRecording = true;
  }

  markActionStart() {
    if (this.actionStartFrame === null && this.currentFrame > 0) {
      this.actionStartFrame = this.currentFrame;
    }
  }

  recordFrame() {
    if (!this.isRecording || this.isReplaying) return;

    // Cap buffer at 1500 frames (approx 25 seconds) to prevent unlimited memory growth
    if (this.recordedFrames.length > 1500) {
      this.recordedFrames.shift();
    }

    const frameData = {
      frameIndex: this.currentFrame,
      camera: {
        x: this.game.camera.x,
        y: this.game.camera.y,
        zoom: this.game.camera.zoom
      },
      worms: this.game.worms.map(w => ({
        name: w.name,
        teamName: w.teamName,
        teamColor: w.teamColor,
        x: w.x,
        y: w.y,
        vx: w.vx,
        vy: w.vy,
        facingDir: w.facingDir,
        aimAngle: w.aimAngle,
        health: w.health,
        maxHealth: w.maxHealth || 100,
        isGrave: w.isGrave,
        isFalling: w.isFalling,
        isDrowned: w.isDrowned,
        isActive: (w === this.game.activeWorm) && (this.game.state === 'PLAYING' || this.game.state === 'FIRING'),
        rope: (w.rope && w.rope.attached) ? { attached: true, x: w.rope.x, y: w.rope.y } : null
      })),
      projectiles: this.game.projectiles.map(p => ({
        x: p.x,
        y: p.y,
        vx: p.vx,
        vy: p.vy,
        type: p.type,
        radius: p.radius,
        fuse: p.fuse,
        contactFuse: p.contactFuse,
        homingTargetX: p.homingTargetX,
        homingTargetY: p.homingTargetY
      })),
      particles: this.game.particles.list.map(p => ({
        x: p.x,
        y: p.y,
        type: p.type,
        color: p.color,
        size: p.size,
        life: p.life,
        maxLife: p.maxLife,
        text: p.text
      })),
      targeting: {
        state: this.game.state,
        weaponId: (this.game.WEAPONS && this.game.WEAPONS[this.game.selectedWeaponIndex])
          ? this.game.WEAPONS[this.game.selectedWeaponIndex].id
          : 'bazooka',
        isCharging: this.game.isCharging,
        chargePower: this.game.chargePower,
        homingTargetX: this.game.homingTargetX,
        homingTargetY: this.game.homingTargetY,
        mouseCanvasX: this.game.mouse ? this.game.mouse.canvasX : 0,
        mouseCanvasY: this.game.mouse ? this.game.mouse.canvasY : 0
      },
      waterTime: performance.now() * 0.002
    };

    this.recordedFrames.push(frameData);
    this.currentFrame++;
  }

  recordCarve(x, y, radius) {
    if (!this.isRecording || this.isReplaying) return;
    this.recordedCarves.push({
      frameIndex: this.currentFrame,
      x,
      y,
      radius
    });
  }

  recordAudio(soundName, radius = null) {
    if (!this.isRecording || this.isReplaying) return;
    this.recordedAudio.push({
      frameIndex: this.currentFrame,
      sound: soundName,
      radius: radius
    });
  }

  saveWinningReplay(winningTeam) {
    this.isRecording = false;

    if (!this.recordedFrames || this.recordedFrames.length < 10) {
      return false;
    }

    // Determine start frame for the action replay:
    let startIdx = 0;
    if (this.actionStartFrame !== null && this.actionStartFrame > 65) {
      const targetFrame = Math.max(0, this.actionStartFrame - 65);
      const arrayIdx = this.recordedFrames.findIndex(f => f.frameIndex >= targetFrame);
      startIdx = arrayIdx !== -1 ? arrayIdx : 0;
    } else {
      // Find where any action/movement began in recordedFrames
      let firstActionIdx = null;
      for (let i = 0; i < this.recordedFrames.length; i++) {
        const f = this.recordedFrames[i];
        if (f.projectiles && f.projectiles.length > 0) {
          firstActionIdx = i;
          break;
        }
        if (f.worms) {
          const moving = f.worms.some(w => !w.isGrave && (w.isFalling || Math.hypot(w.vx, w.vy) > 0.3 || (w.rope && w.rope.attached)));
          if (moving) {
            firstActionIdx = i;
            break;
          }
        }
      }
      if (firstActionIdx !== null && firstActionIdx > 65) {
        startIdx = Math.max(0, firstActionIdx - 65);
      } else if (this.recordedFrames.length > 400) {
        startIdx = Math.max(0, this.recordedFrames.length - 360);
      }
    }

    const trimmedFrames = this.recordedFrames.slice(startIdx);
    if (trimmedFrames.length < 10) {
      return false;
    }

    const startFrameIndex = trimmedFrames[0].frameIndex;

    // Clone initial terrain snapshot and pre-apply any carves that happened before startFrameIndex
    const savedTerrain = document.createElement('canvas');
    if (this.initialTerrainSnapshot) {
      savedTerrain.width = this.initialTerrainSnapshot.width;
      savedTerrain.height = this.initialTerrainSnapshot.height;
      const sCtx = savedTerrain.getContext('2d');
      sCtx.drawImage(this.initialTerrainSnapshot, 0, 0);

      // Pre-apply earlier carves
      sCtx.save();
      sCtx.globalCompositeOperation = 'destination-out';
      sCtx.fillStyle = 'rgba(0,0,0,1)';
      for (const carve of this.recordedCarves) {
        if (carve.frameIndex < startFrameIndex) {
          sCtx.beginPath();
          sCtx.arc(carve.x, carve.y, carve.radius, 0, Math.PI * 2);
          sCtx.fill();
        }
      }
      sCtx.restore();
    }

    // Filter and adjust carves and audio for trimmed frames
    const adjustedCarves = this.recordedCarves
      .filter(c => c.frameIndex >= startFrameIndex)
      .map(c => ({ ...c, frameIndex: c.frameIndex - startFrameIndex }));

    const adjustedAudio = this.recordedAudio
      .filter(a => a.frameIndex >= startFrameIndex)
      .map(a => ({ ...a, frameIndex: a.frameIndex - startFrameIndex }));

    const adjustedFrames = trimmedFrames.map(f => ({
      ...f,
      frameIndex: f.frameIndex - startFrameIndex
    }));

    // Identify the exact frame index of the fatal impact / final death / drowning
    let fatalImpactFrame = null;
    
    // 1. Scan for the frame where the worm actually died or drowned (health <= 0, isGrave, or isDrowned)
    for (let i = 0; i < adjustedFrames.length; i++) {
      const f = adjustedFrames[i];
      if (f.worms) {
        for (const w of f.worms) {
          if (w.health <= 0 || w.isGrave || w.isDrowned) {
            fatalImpactFrame = i;
            break;
          }
        }
      }
      if (fatalImpactFrame !== null) break;
    }

    // 2. Fallback: check for first frame of damage if no 0-health frame found
    if (fatalImpactFrame === null) {
      const initialHealth = {};
      if (adjustedFrames[0] && adjustedFrames[0].worms) {
        adjustedFrames[0].worms.forEach(w => {
          initialHealth[w.name] = w.health;
        });
      }
      for (let i = 0; i < adjustedFrames.length; i++) {
        const f = adjustedFrames[i];
        if (f.worms) {
          for (const w of f.worms) {
            if (initialHealth[w.name] !== undefined && w.health < initialHealth[w.name]) {
              fatalImpactFrame = i;
              break;
            }
          }
        }
        if (fatalImpactFrame !== null) break;
      }
    }

    if (fatalImpactFrame === null && adjustedCarves && adjustedCarves.length > 0) {
      const firstCarveIdx = adjustedFrames.findIndex(f => f.frameIndex >= adjustedCarves[0].frameIndex);
      if (firstCarveIdx !== -1) {
        fatalImpactFrame = firstCarveIdx;
      }
    }

    if (fatalImpactFrame === null) {
      fatalImpactFrame = Math.max(0, adjustedFrames.length - 60);
    }

    this.winningReplay = {
      frames: adjustedFrames,
      carves: adjustedCarves,
      audio: adjustedAudio,
      metadata: {
        ...this.turnMetadata,
        winningTeam: winningTeam
      },
      initialTerrain: savedTerrain,
      fatalImpactFrame: fatalImpactFrame
    };

    return true;
  }

  hasWinningReplay() {
    return !!(this.winningReplay && this.winningReplay.frames && this.winningReplay.frames.length > 5);
  }

  // ─── Playback Methods ────────────────────────────────────────────────────────

  playWinningReplay(onComplete = null) {
    if (!this.hasWinningReplay()) {
      if (onComplete) onComplete();
      return;
    }

    this.onReplayComplete = onComplete;
    this.isReplaying = true;
    this.playbackIndex = 0;
    this.playbackSpeed = 1.0;
    this.isPaused = false;
    this.endHoldTimer = 0;

    // Hide game HUD & game over screen while replaying
    const hud = document.getElementById('game-hud');
    const gameOverScreen = document.getElementById('game-over-screen');
    const pauseMenu = document.getElementById('pause-menu-overlay');
    if (hud) hud.classList.add('hidden');
    if (gameOverScreen) gameOverScreen.classList.add('hidden');
    if (pauseMenu) pauseMenu.classList.add('hidden');

    // Setup Replay Terrain Canvas
    this.resetPlaybackTerrain();

    // Show Action Replay Overlay
    this.showReplayUI();
  }

  resetPlaybackTerrain() {
    if (!this.playbackTerrainCanvas) {
      this.playbackTerrainCanvas = document.createElement('canvas');
      this.playbackTerrainCanvas.width = this.game.width;
      this.playbackTerrainCanvas.height = this.game.height;
      this.playbackTerrainCtx = this.playbackTerrainCanvas.getContext('2d');
    }

    this.playbackTerrainCtx.clearRect(0, 0, this.game.width, this.game.height);
    if (this.winningReplay && this.winningReplay.initialTerrain) {
      this.playbackTerrainCtx.drawImage(this.winningReplay.initialTerrain, 0, 0);
    }

    this.appliedCarveIndex = 0;
    this.appliedAudioIndex = 0;
  }

  showReplayUI() {
    const overlay = document.getElementById('action-replay-overlay');
    if (!overlay) return;
    overlay.classList.remove('hidden');

    const killHighlight = document.getElementById('replay-worm-highlight');
    const killInfo = document.getElementById('replay-kill-info');

    if (this.winningReplay && this.winningReplay.metadata) {
      const meta = this.winningReplay.metadata;
      if (killHighlight) {
        killHighlight.textContent = meta.activeWormName || 'Worm';
        killHighlight.style.color = meta.activeTeamColor || '#f59e0b';
      }
      if (killInfo) {
        if (meta.winningTeam === 'Draw') {
          killInfo.innerHTML = `⚔️ <strong>Mutual Destruction!</strong> Both teams wiped out!`;
        } else if (meta.winningTeam && meta.activeTeamName && meta.winningTeam !== meta.activeTeamName) {
          killInfo.innerHTML = `🌊 <strong style="color:${meta.activeTeamColor}">${meta.activeWormName}</strong> took a fatal plunge! Victory for <strong>${meta.winningTeam}</strong>!`;
        } else {
          killInfo.innerHTML = `🏆 <strong style="color:${meta.activeTeamColor}">${meta.activeWormName}</strong> delivered victory for <strong>${meta.winningTeam || meta.activeTeamName}</strong> with ${meta.weaponIcon || '🚀'} ${meta.weaponName || 'Bazooka'}!`;
        }
      }
    }

    this.updatePlayPauseButton();
    this.updateSpeedButton();
  }

  hideReplayUI() {
    const overlay = document.getElementById('action-replay-overlay');
    if (overlay) overlay.classList.add('hidden');
  }

  restart() {
    this.playbackIndex = 0;
    this.endHoldTimer = 0;
    this.isPaused = false;
    this.resetPlaybackTerrain();
    this.updatePlayPauseButton();
  }

  togglePause() {
    this.isPaused = !this.isPaused;
    this.updatePlayPauseButton();
  }

  toggleSpeed() {
    // Cycle speeds: 1.0 -> 1.25 -> 1.5 -> 0.5 (Slow-Mo) -> 1.0
    if (this.playbackSpeed === 1.0) {
      this.playbackSpeed = 1.25;
    } else if (this.playbackSpeed === 1.25) {
      this.playbackSpeed = 1.5;
    } else if (this.playbackSpeed === 1.5) {
      this.playbackSpeed = 0.5;
    } else {
      this.playbackSpeed = 1.0;
    }
    this.updateSpeedButton();
  }

  updatePlayPauseButton() {
    const icon = document.getElementById('replay-playpause-icon');
    const text = document.getElementById('replay-playpause-text');
    if (icon) icon.textContent = this.isPaused ? '▶️' : '⏸️';
    if (text) text.textContent = this.isPaused ? 'Play' : 'Pause';
  }

  updateSpeedButton() {
    const speedText = document.getElementById('replay-speed-text');
    const speedBtn = document.getElementById('replay-speed-btn');
    if (speedText) {
      if (this.playbackSpeed === 0.5) {
        speedText.textContent = '0.5x (Slow-Mo)';
        if (speedBtn) speedBtn.classList.add('slow-mo-active');
      } else if (this.playbackSpeed === 1.25) {
        speedText.textContent = '1.25x (Fast)';
        if (speedBtn) speedBtn.classList.remove('slow-mo-active');
      } else if (this.playbackSpeed === 1.5) {
        speedText.textContent = '1.5x (Turbo)';
        if (speedBtn) speedBtn.classList.remove('slow-mo-active');
      } else {
        speedText.textContent = '1.0x Normal';
        if (speedBtn) speedBtn.classList.remove('slow-mo-active');
      }
    }
  }

  seek(ratio) {
    if (!this.winningReplay || !this.winningReplay.frames.length) return;
    const targetIdx = Math.floor(ratio * (this.winningReplay.frames.length - 1));
    this.playbackIndex = Math.max(0, Math.min(targetIdx, this.winningReplay.frames.length - 1));

    // Rebuild terrain carves up to target frame
    this.resetPlaybackTerrain();
    const currentFrame = this.winningReplay.frames[this.playbackIndex];
    if (currentFrame) {
      const carves = this.winningReplay.carves;
      for (let i = 0; i < carves.length; i++) {
        if (carves[i].frameIndex <= currentFrame.frameIndex) {
          this.applyCarveToPlayback(carves[i]);
          this.appliedCarveIndex = i + 1;
        }
      }

      // Fast forward audio index
      const audios = this.winningReplay.audio;
      for (let i = 0; i < audios.length; i++) {
        if (audios[i].frameIndex <= currentFrame.frameIndex) {
          this.appliedAudioIndex = i + 1;
        }
      }
    }
  }

  skip() {
    this.finishReplay();
  }

  finishReplay() {
    this.isReplaying = false;
    this.hideReplayUI();

    if (this.onReplayComplete) {
      const cb = this.onReplayComplete;
      this.onReplayComplete = null;
      cb();
    } else {
      this.game.ui.showGameOver(this.winningReplay ? this.winningReplay.metadata.winningTeam : 'Victory');
    }
  }

  applyCarveToPlayback(carve) {
    if (!this.playbackTerrainCtx) return;
    this.playbackTerrainCtx.save();
    this.playbackTerrainCtx.globalCompositeOperation = 'destination-out';
    this.playbackTerrainCtx.fillStyle = 'rgba(0,0,0,1)';
    this.playbackTerrainCtx.beginPath();
    this.playbackTerrainCtx.arc(carve.x, carve.y, carve.radius, 0, Math.PI * 2);
    this.playbackTerrainCtx.fill();
    this.playbackTerrainCtx.restore();
  }

  // ─── Playback Update & Render Loop ──────────────────────────────────────────

  update(dt) {
    if (!this.isReplaying || !this.winningReplay || !this.winningReplay.frames.length) return;

    const frames = this.winningReplay.frames;
    const totalFrames = frames.length;

    if (this.playbackIndex >= totalFrames - 1) {
      // Hold at end for a moment (1.5 seconds) then auto-transition
      this.endHoldTimer += dt * 16.666;
      if (this.endHoldTimer > 1800) {
        this.finishReplay();
      }
      return;
    }

    if (!this.isPaused) {
      // ONLY slow down the ~2 seconds (120 frames) leading up to and during final damage impact!
      // All other parts of the replay (aim, weapon charge, missile launch/flight) play at full normal speed (1.0x)
      const fatalImpactFrame = (this.winningReplay && this.winningReplay.fatalImpactFrame !== undefined) 
        ? this.winningReplay.fatalImpactFrame 
        : Math.max(0, totalFrames - 90);

      const slowMoStart = Math.max(0, fatalImpactFrame - 120);
      const slowMoEnd = Math.min(totalFrames - 1, fatalImpactFrame + 30);
      
      const inSlowMoZone = (this.playbackIndex >= slowMoStart && this.playbackIndex <= slowMoEnd);
      let effectiveSpeed = this.playbackSpeed;

      if (inSlowMoZone) {
        // Slow down to 0.45x bullet-time exclusively in the 2 seconds right before and at final damage
        effectiveSpeed *= 0.45;
      }

      this.playbackIndex += dt * effectiveSpeed;
      const currIdx = Math.min(Math.floor(this.playbackIndex), totalFrames - 1);

      // Update badge text and glow when in slow-mo zone
      const titleTextEl = document.getElementById('replay-title-text');
      if (titleTextEl) {
        titleTextEl.textContent = inSlowMoZone ? 'SLOW-MO IMPACT' : 'ACTION REPLAY';
      }
      const recBadge = document.querySelector('.replay-rec-badge');
      if (recBadge) {
        if (inSlowMoZone) {
          recBadge.classList.add('slow-mo-badge-active');
        } else {
          recBadge.classList.remove('slow-mo-badge-active');
        }
      }

      const currentFrame = frames[currIdx];
      if (currentFrame) {
        // Apply carves up to current frame
        const carves = this.winningReplay.carves;
        while (this.appliedCarveIndex < carves.length && carves[this.appliedCarveIndex].frameIndex <= currentFrame.frameIndex) {
          this.applyCarveToPlayback(carves[this.appliedCarveIndex]);
          this.appliedCarveIndex++;
        }

        // Trigger sound effects up to current frame
        const audios = this.winningReplay.audio;
        while (this.appliedAudioIndex < audios.length && audios[this.appliedAudioIndex].frameIndex <= currentFrame.frameIndex) {
          const audioEvent = audios[this.appliedAudioIndex];
          if (this.game.audio) {
            this.game.audio.play(audioEvent.sound);
          }
          this.appliedAudioIndex++;
        }
      }
    }

    // Update UI Progress Bar & Timer
    const currIdx = Math.min(Math.floor(this.playbackIndex), totalFrames - 1);
    const progress = (currIdx / Math.max(1, totalFrames - 1)) * 100;

    const progressBar = document.getElementById('replay-timeline-bar');
    if (progressBar) {
      progressBar.style.width = `${progress}%`;
    }

    const timeDisplay = document.getElementById('replay-time-display');
    if (timeDisplay) {
      const elapsedSec = (currIdx * 16.666) / 1000;
      const mins = Math.floor(elapsedSec / 60);
      const secs = (elapsedSec % 60).toFixed(2);
      const minStr = mins < 10 ? `0${mins}` : mins;
      const secStr = (elapsedSec % 60) < 10 ? `0${secs}` : secs;
      timeDisplay.textContent = `${minStr}:${secStr}`;
    }
  }

  render(ctx) {
    if (!this.isReplaying || !this.winningReplay || !this.winningReplay.frames.length) return;

    const frames = this.winningReplay.frames;
    const frameIdx = Math.min(Math.floor(this.playbackIndex), frames.length - 1);
    const frame = frames[frameIdx];
    if (!frame) return;

    ctx.clearRect(0, 0, this.game.canvas.width, this.game.canvas.height);

    ctx.save();
    ctx.scale(frame.camera.zoom, frame.camera.zoom);
    ctx.translate(-frame.camera.x, -frame.camera.y);

    // 1. Draw Background
    this.drawBackground(ctx, frame.camera);

    // 2. Draw Terrain from Playback Canvas
    if (this.playbackTerrainCanvas) {
      ctx.drawImage(this.playbackTerrainCanvas, 0, 0);
    }

    // 3. Draw Water Waves
    this.drawWater(ctx, frame.camera, frame.waterTime);

    // 4. Draw Particles
    this.drawParticles(ctx, frame.particles);

    // 5. Draw Projectiles
    this.drawProjectiles(ctx, frame.projectiles);

    // 6. Draw Worms
    this.drawWorms(ctx, frame.worms);

    // 7. Draw Targeting Crosshairs / Lines
    this.drawTargetingLine(ctx, frame.targeting, frame.worms);

    ctx.restore();
  }

  // ─── Drawing Subroutines ─────────────────────────────────────────────────────

  drawBackground(ctx, camera) {
    const gradient = ctx.createLinearGradient(camera.x, 0, camera.x, this.game.height);
    gradient.addColorStop(0, '#1e293b');
    gradient.addColorStop(0.5, '#0f172a');
    gradient.addColorStop(1, '#020617');
    ctx.fillStyle = gradient;
    const visibleWidth = this.game.canvas.width / camera.zoom;
    ctx.fillRect(camera.x, 0, visibleWidth, this.game.height);

    ctx.fillStyle = 'rgba(255, 255, 255, 0.03)';
    for (let i = 0; i < 6; i++) {
      const cx = ((i * 300) + (camera.x * 0.3)) % (this.game.width + 200) - 100;
      const cy = 100 + Math.sin(i) * 50;
      ctx.beginPath();
      ctx.arc(cx, cy, 50, 0, Math.PI * 2);
      ctx.arc(cx + 40, cy - 10, 45, 0, Math.PI * 2);
      ctx.arc(cx - 30, cy + 10, 35, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  drawWater(ctx, camera, waterTime) {
    const time = waterTime || performance.now() * 0.002;
    ctx.fillStyle = 'rgba(14, 116, 144, 0.8)';
    const visibleWidth = this.game.canvas.width / camera.zoom;
    const startX = camera.x - 600;
    const endX = camera.x + visibleWidth + 600;

    ctx.beginPath();
    ctx.moveTo(startX, this.game.waterLevel);

    for (let x = startX; x <= endX; x += 20) {
      const waveHeight = Math.sin(x * 0.015 + time) * 6 + Math.cos(x * 0.03 - time) * 3;
      ctx.lineTo(x, this.game.waterLevel + waveHeight);
    }

    ctx.lineTo(endX, this.game.height + 200);
    ctx.lineTo(startX, this.game.height + 200);
    ctx.closePath();
    ctx.fill();
  }

  drawParticles(ctx, particles) {
    if (!particles) return;
    particles.forEach(p => {
      ctx.save();
      const lifeRatio = p.maxLife > 0 ? (p.life / p.maxLife) : 1;
      ctx.globalAlpha = Math.max(0, Math.min(1, lifeRatio));

      if (p.type === 'fire') {
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = '#f59e0b';
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * 0.6, 0, Math.PI * 2);
        ctx.fill();
      }
      else if (p.type === 'smoke' || p.type === 'smoke_trail') {
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      }
      else if (p.type === 'water' || p.type === 'dirt') {
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      }
      else if (p.type === 'text') {
        ctx.font = '800 11px Space Grotesk';
        ctx.textAlign = 'center';
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 3;
        ctx.strokeText(p.text, p.x, p.y);
        ctx.fillStyle = p.color;
        ctx.fillText(p.text, p.x, p.y);
      }

      ctx.restore();
    });
  }

  drawProjectiles(ctx, projectiles) {
    if (!projectiles) return;
    projectiles.forEach(p => {
      ctx.save();

      if (p.type === 'bazooka' || p.type === 'airstrike_missile') {
        ctx.translate(p.x, p.y);
        ctx.rotate(Math.atan2(p.vy, p.vx));

        ctx.fillStyle = '#dc2626';
        ctx.beginPath();
        ctx.moveTo(8, 0); ctx.lineTo(2, -3); ctx.lineTo(2, 3);
        ctx.closePath();
        ctx.fill();

        ctx.fillStyle = '#f1f5f9';
        ctx.fillRect(-6, -2.5, 8, 5);

        ctx.fillStyle = '#475569';
        ctx.fillRect(-8, -3.5, 2, 7);
      } else if (p.type === 'grenade' || p.type === 'cluster' || p.type === 'cluster_shrapnel') {
        ctx.fillStyle = p.type === 'cluster_shrapnel' ? '#f59e0b' : '#166534';
        ctx.strokeStyle = '#f1f5f9';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius || 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      } else if (p.type === 'banana' || p.type === 'banana_shrapnel') {
        const isBaby = p.type === 'banana_shrapnel';
        const speed = Math.sqrt((p.vx || 0) * (p.vx || 0) + (p.vy || 0) * (p.vy || 0));
        const spin = (p.x * 0.08 + performance.now() * 0.007 * ((p.vx || 0) >= 0 ? 1 : -1)) % (Math.PI * 2);
        drawBananaSprite(ctx, p.x, p.y, p.radius || (isBaby ? 4.5 : 7), spin, isBaby);
      } else if (p.type === 'super_sheep') {
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(Math.atan2(p.vy, p.vx));

        ctx.fillStyle = '#ef4444';
        ctx.beginPath();
        ctx.moveTo(-6, 0); ctx.lineTo(-14, -6); ctx.lineTo(-11, 6);
        ctx.closePath();
        ctx.fill();

        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(-3, 4); ctx.lineTo(-3, 7);
        ctx.moveTo(3, 4); ctx.lineTo(3, 7);
        ctx.stroke();

        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(0, 0, p.radius || 7, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(-3, -2, 3.5, 0, Math.PI * 2);
        ctx.arc(3, 1, 3.5, 0, Math.PI * 2);
        ctx.arc(-2, 2, 3, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = '#fca5a5';
        ctx.beginPath();
        ctx.arc((p.radius || 7) - 1, -1, 3.5, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = '#000000';
        ctx.beginPath();
        ctx.arc(p.radius || 7, -2, 0.7, 0, Math.PI * 2);
        ctx.fill();

        ctx.restore();
      } else if (p.type === 'holy') {
        ctx.fillStyle = '#fbbf24';
        ctx.strokeStyle = '#78350f';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius || 6, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(p.x, p.y - (p.radius || 6));
        ctx.lineTo(p.x, p.y - (p.radius || 6) - 5);
        ctx.moveTo(p.x - 3, p.y - (p.radius || 6) - 3);
        ctx.lineTo(p.x + 3, p.y - (p.radius || 6) - 3);
        ctx.stroke();
      } else if (p.type === 'homing_missile') {
        ctx.translate(p.x, p.y);
        ctx.rotate(Math.atan2(p.vy, p.vx));

        const pulse = 0.5 + 0.5 * Math.sin(performance.now() * 0.012);
        ctx.strokeStyle = `rgba(255, ${Math.round(50 + 180 * pulse)}, 0, ${0.5 + 0.5 * pulse})`;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(0, 0, 10 + pulse * 4, 0, Math.PI * 2);
        ctx.stroke();

        ctx.fillStyle = '#f97316';
        ctx.beginPath();
        ctx.moveTo(10, 0); ctx.lineTo(4, -3); ctx.lineTo(4, 3);
        ctx.closePath();
        ctx.fill();

        ctx.fillStyle = '#0891b2';
        ctx.fillRect(-7, -2.5, 11, 5);

        ctx.fillStyle = '#0f172a';
        ctx.fillRect(-9, -4, 2.5, 8);

        const glow = 0.6 + 0.4 * Math.sin(performance.now() * 0.02);
        ctx.fillStyle = `rgba(251, 191, 36, ${glow})`;
        ctx.beginPath();
        ctx.arc(-9, 0, 3 * glow, 0, Math.PI * 2);
        ctx.fill();
      } else if (p.type === 'dynamite') {
        ctx.fillStyle = '#dc2626';
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 1;
        ctx.fillRect(p.x - 3, p.y - 5, 6, 10);
        ctx.strokeRect(p.x - 3, p.y - 5, 6, 10);

        const time = performance.now() * 0.05;
        ctx.strokeStyle = time % 2 < 1 ? '#fbbf24' : '#ef4444';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(p.x, p.y - 5);
        ctx.quadraticCurveTo(p.x + 3, p.y - 9, p.x + 4, p.y - 8);
        ctx.stroke();
      }

      if ((!p.contactFuse || p.type === 'super_sheep') && p.type !== 'banana_shrapnel' && p.type !== 'cluster_shrapnel' && p.fuse > 0) {
        const secs = Math.ceil(p.fuse);
        ctx.font = '800 10px Space Grotesk';
        ctx.textAlign = 'center';
        ctx.fillStyle = 'rgba(15, 23, 42, 0.75)';
        ctx.fillRect(p.x - 8, p.y - (p.radius || 4) - 17, 16, 12);
        ctx.fillStyle = '#f59e0b';
        ctx.fillText(`${secs}`, p.x, p.y - (p.radius || 4) - 7);
      }

      ctx.restore();
    });
  }

  drawWorms(ctx, worms) {
    if (!worms) return;
    worms.forEach(w => {
      if (w.isGrave) {
        if (w.isDrowned || w.y >= this.game.waterLevel - 5) {
          return; // Drowned worms sink under water
        }
        ctx.fillStyle = '#64748b';
        ctx.fillRect(w.x - 5, w.y - 6, 10, 14);
        ctx.fillStyle = '#475569';
        ctx.fillRect(w.x - 3, w.y - 1, 6, 2);
        ctx.fillRect(w.x - 1, w.y - 4, 2, 8);
        return;
      }

      ctx.save();

      // Draw active rope
      if (w.rope && w.rope.attached) {
        ctx.strokeStyle = '#d97706';
        ctx.lineWidth = 2.5;
        ctx.setLineDash([6, 3]);
        ctx.beginPath();
        ctx.moveTo(w.x, w.y - 2);
        ctx.lineTo(w.rope.x, w.rope.y);
        ctx.stroke();
        ctx.setLineDash([]);

        ctx.fillStyle = '#475569';
        ctx.beginPath();
        ctx.arc(w.rope.x, w.rope.y, 4, 0, Math.PI * 2);
        ctx.fill();
      }

      const halfH = 12;

      // Worm body capsule
      ctx.fillStyle = '#ff8fa3';
      ctx.beginPath();
      ctx.arc(w.x, w.y - halfH + 5, 6, Math.PI, 0, false);
      ctx.lineTo(w.x + 6, w.y + halfH);
      ctx.lineTo(w.x - 6, w.y + halfH);
      ctx.closePath();
      ctx.fill();

      // Team headband
      ctx.fillStyle = w.teamColor;
      ctx.fillRect(w.x - 6, w.y - halfH + 1.5, 12, 3);

      // Knot tails
      ctx.beginPath();
      const knotX = w.x - w.facingDir * 6;
      const knotY = w.y - halfH + 3;
      ctx.arc(knotX, knotY, 2, 0, Math.PI * 2);
      ctx.fill();

      // Belly banding
      ctx.strokeStyle = '#f472b6';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(w.x - 4, w.y + 2); ctx.lineTo(w.x + 4, w.y + 2);
      ctx.moveTo(w.x - 5, w.y + 5); ctx.lineTo(w.x + 5, w.y + 5);
      ctx.stroke();

      // Eyes
      const eyeOffsetX = w.facingDir * 3;
      const eyeOffsetY = -4;
      if (w.health <= 0) {
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 1.5;
        const eyeX = w.x + eyeOffsetX;
        const eyeY = w.y + eyeOffsetY;
        ctx.beginPath();
        ctx.moveTo(eyeX - 2, eyeY - 2); ctx.lineTo(eyeX + 2, eyeY + 2);
        ctx.moveTo(eyeX + 2, eyeY - 2); ctx.lineTo(eyeX - 2, eyeY + 2);
        ctx.stroke();
      } else {
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(w.x + eyeOffsetX, w.y + eyeOffsetY, 2.5, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = '#000000';
        const pupilX = w.x + eyeOffsetX + w.facingDir * 0.8;
        const pupilY = w.y + eyeOffsetY + Math.sin(w.aimAngle) * 0.8;
        ctx.beginPath();
        ctx.arc(pupilX, pupilY, 1, 0, Math.PI * 2);
        ctx.fill();
      }

      // Active worm indicator
      if (w.isActive && w.health > 0) {
        const time = performance.now() * 0.006;
        const bounce = Math.sin(time) * 4 - 24;

        ctx.fillStyle = '#10b981';
        ctx.strokeStyle = '#022c22';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(w.x, w.y + bounce);
        ctx.lineTo(w.x - 6, w.y + bounce - 8);
        ctx.lineTo(w.x - 2, w.y + bounce - 8);
        ctx.lineTo(w.x - 2, w.y + bounce - 14);
        ctx.lineTo(w.x + 2, w.y + bounce - 14);
        ctx.lineTo(w.x + 2, w.y + bounce - 8);
        ctx.lineTo(w.x + 6, w.y + bounce - 8);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
      }

      // Name & Health bar
      ctx.font = 'bold 9px Space Grotesk';
      ctx.textAlign = 'center';

      ctx.fillStyle = 'rgba(15, 23, 42, 0.55)';
      ctx.fillRect(w.x - 30, w.y - halfH - 24, 60, 13);

      ctx.fillStyle = w.teamColor === '#ef4444' ? '#fca5a5' : '#93c5fd';
      ctx.fillText(w.name, w.x, w.y - halfH - 14);

      const hbW = 34;
      const hbH = 3;
      const hbX = w.x - hbW / 2;
      const hbY = w.y - halfH - 9;

      ctx.fillStyle = 'rgba(0,0,0,0.6)';
      ctx.fillRect(hbX, hbY, hbW, hbH);

      const pct = Math.max(0, w.health / 100);
      if (pct > 0.5) ctx.fillStyle = '#10b981';
      else if (pct > 0.2) ctx.fillStyle = '#fbbf24';
      else ctx.fillStyle = '#ef4444';
      ctx.fillRect(hbX, hbY, hbW * pct, hbH);

      ctx.strokeStyle = 'rgba(255,255,255,0.15)';
      ctx.lineWidth = 0.5;
      ctx.strokeRect(hbX, hbY, hbW, hbH);

      ctx.restore();
    });
  }

  drawTargetingLine(ctx, targeting, worms) {
    if (!targeting) return;
    const weaponId = targeting.weaponId;

    if (weaponId === 'homing_missile' && targeting.homingTargetX !== undefined) {
      const tx = targeting.homingTargetX;
      const ty = targeting.homingTargetY;
      const activeWorm = worms ? worms.find(w => w.isActive) : null;
      const now = performance.now();

      if (activeWorm) {
        ctx.strokeStyle = 'rgba(8, 145, 178, 0.5)';
        ctx.lineWidth = 1.5;
        ctx.setLineDash([6, 5]);
        ctx.beginPath();
        ctx.moveTo(activeWorm.x, activeWorm.y);
        ctx.lineTo(tx, ty);
        ctx.stroke();
        ctx.setLineDash([]);

        const pulse = 0.5 + 0.5 * Math.sin(now * 0.008);
        const lockR = 14 + pulse * 4;
        ctx.strokeStyle = `rgba(8, 145, 178, ${0.6 + 0.4 * pulse})`;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(tx, ty, lockR, 0, Math.PI * 2);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(tx - lockR - 6, ty); ctx.lineTo(tx + lockR + 6, ty);
        ctx.moveTo(tx, ty - lockR - 6); ctx.lineTo(tx, ty + lockR + 6);
        ctx.stroke();
      }
    }
  }
}
